/**
 * LineItemGrid (U5) — the flagship estimate-entry grid. Custom built, no grid
 * dependency. Spreadsheet-grade keyboard model:
 *
 *   • roving tabindex — the grid is ONE tab stop; arrows move the focused cell
 *   • Enter / F2 begins editing; typing a character begins editing seeded
 *     with that character (quick-type, like a spreadsheet)
 *   • Enter commits and moves DOWN a row (adds a row at the bottom when the
 *     host provides onAddRow); Shift+Enter commits upward
 *   • Tab / Shift+Tab while editing commit and move right / left
 *   • Escape cancels the edit (closes the type-ahead first if open)
 *   • Backspace/Delete on a focused cell opens an empty edit
 *   • ⌘⌫ (mac) / Ctrl+Delete deletes the row
 *   • type-ahead picker slot: columns may supply getSuggestions() — matches
 *     render in a glass dropdown; ↑/↓ navigate, Enter picks
 *
 * Values in/out are plain strings/numbers; parsing here is input hygiene
 * only. All money math is the shared engine's (U3) — computed cells should
 * be provided by the host as readonly columns.
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cx } from './cx';
import { IconClose, IconPlus } from './icons';
import { formatCurrency, formatNumber, parseNumeric } from './format';

export type LineGridValue = string | number | null;

export interface LineGridSuggestion {
  id: string;
  label: string;
  /** Right-aligned mono detail, e.g. "$78.00 / hr". */
  detail?: string;
  /** Group header, e.g. "Labor roles" / "Materials" / "Assemblies". */
  group?: string;
}

export interface LineGridColumn<Row extends { id: string }> {
  key: string;
  header: ReactNode;
  /** Parsing/format/alignment preset. Default 'text'. */
  kind?: 'text' | 'number' | 'currency';
  /** Display decimals for kind 'number'. Default 2 (trailing zeros trimmed). */
  decimals?: number;
  /** CSS grid track, e.g. '7rem' or 'minmax(16rem, 3fr)'. */
  width?: string;
  /** Default: numbers/currency align end. */
  align?: 'start' | 'end';
  editable?: boolean | ((row: Row) => boolean);
  placeholder?: string;
  getValue: (row: Row) => LineGridValue;
  /** Custom rest display (defaults derive from kind). */
  format?: (row: Row) => ReactNode;
  /** Type-ahead picker slot for this column. */
  getSuggestions?: (query: string, row: Row) => LineGridSuggestion[];
}

export type LineGridRowTone = 'default' | 'alternate' | 'allowance' | 'subquote';

export interface LineItemGridProps<Row extends { id: string }> {
  columns: ReadonlyArray<LineGridColumn<Row>>;
  rows: ReadonlyArray<Row>;
  /** Fired when an edited cell commits a changed value. */
  onCellCommit: (rowId: string, columnKey: string, value: LineGridValue) => void;
  /** Fired when a type-ahead suggestion is picked (instead of onCellCommit). */
  onSuggestionPick?: (rowId: string, columnKey: string, suggestion: LineGridSuggestion) => void;
  /** Enables Enter-past-last-row + the add-line footer button. */
  onAddRow?: () => void;
  /** Enables ⌘⌫ and the hover delete affordance. */
  onDeleteRow?: (rowId: string) => void;
  rowTone?: (row: Row) => LineGridRowTone | undefined;
  /** Totals bar etc., rendered inside the grid frame. */
  footer?: ReactNode;
  emptyHint?: string;
  addLabel?: string;
  'aria-label': string;
  className?: string;
}

interface CellAddress {
  r: number;
  c: number;
}

interface EditState {
  draft: string;
  /** Edit began by typing — arrows commit+move instead of moving the caret. */
  viaTyping: boolean;
  suggestActive: number;
  /** Escape closed the type-ahead; typing re-opens it. */
  suggestClosed: boolean;
}

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
}

export function LineItemGrid<Row extends { id: string }>({
  columns,
  rows,
  onCellCommit,
  onSuggestionPick,
  onAddRow,
  onDeleteRow,
  rowTone,
  footer,
  emptyHint = 'No lines yet.',
  addLabel = 'Add line',
  'aria-label': ariaLabel,
  className,
}: LineItemGridProps<Row>): JSX.Element {
  const uid = useId();
  const [focus, setFocus] = useState<CellAddress>({ r: 0, c: 0 });
  const [edit, setEdit] = useState<EditState | null>(null);
  const cellRefs = useRef(new Map<string, HTMLDivElement>());
  const inputRef = useRef<HTMLInputElement>(null);
  const wantFocus = useRef(false);
  const pendingRow = useRef<number | null>(null);
  const prevRowCount = useRef(rows.length);
  const isEditing = edit !== null;

  const template = useMemo(() => {
    const tracks = columns.map((col) => col.width ?? 'minmax(6rem, 1fr)');
    return `2.4rem ${tracks.join(' ')}${onDeleteRow ? ' 2.6rem' : ''}`;
  }, [columns, onDeleteRow]);

  // Clamp focus when rows/columns shrink; jump to a freshly added row.
  useEffect(() => {
    if (pendingRow.current !== null && rows.length > prevRowCount.current) {
      const r = Math.min(pendingRow.current, rows.length - 1);
      pendingRow.current = null;
      wantFocus.current = true;
      setFocus((f) => ({ r, c: f.c }));
    } else if (focus.r >= rows.length || focus.c >= columns.length) {
      setFocus({
        r: Math.max(0, Math.min(focus.r, rows.length - 1)),
        c: Math.max(0, Math.min(focus.c, columns.length - 1)),
      });
    }
    prevRowCount.current = rows.length;
  }, [rows.length, columns.length, focus.r, focus.c]);

  // Focus the editor input when an edit begins (caret at end, like a sheet).
  useEffect(() => {
    if (isEditing) {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }
  }, [isEditing]);

  // Restore DOM focus to the anchor cell after navigation / commit-move.
  useEffect(() => {
    if (!isEditing && wantFocus.current) {
      wantFocus.current = false;
      cellRefs.current.get(`${focus.r}:${focus.c}`)?.focus();
    }
  }, [focus, isEditing, rows.length]);

  function colEditable(col: LineGridColumn<Row>, row: Row): boolean {
    if (typeof col.editable === 'function') return col.editable(row);
    return col.editable !== false;
  }

  function moveFocus(dr: number, dc: number): void {
    setFocus((f) => ({
      r: Math.max(0, Math.min(rows.length - 1, f.r + dr)),
      c: Math.max(0, Math.min(columns.length - 1, f.c + dc)),
    }));
    wantFocus.current = true;
  }

  function beginEdit(seed: string | null, viaTyping: boolean): void {
    const row = rows[focus.r];
    const col = columns[focus.c];
    if (!row || !col || !colEditable(col, row)) return;
    const current = col.getValue(row);
    const draft = seed !== null ? seed : current === null ? '' : String(current);
    setEdit({ draft, viaTyping, suggestActive: 0, suggestClosed: false });
  }

  function parseDraft(col: LineGridColumn<Row>, draft: string): LineGridValue | undefined {
    if (col.kind === 'number' || col.kind === 'currency') {
      const parsed = parseNumeric(draft);
      if (parsed !== null && Number.isNaN(parsed)) return undefined; // unparseable → revert
      return parsed;
    }
    const trimmed = draft.trimEnd();
    return trimmed === '' ? null : trimmed;
  }

  function commitEdit(move?: { dr: number; dc: number } | 'add-down'): void {
    const row = rows[focus.r];
    const col = columns[focus.c];
    if (edit && row && col) {
      const value = parseDraft(col, edit.draft);
      if (value !== undefined && value !== col.getValue(row)) {
        onCellCommit(row.id, col.key, value);
      }
    }
    setEdit(null);
    // Only reclaim DOM focus when the commit came from grid navigation —
    // a blur-commit means focus is deliberately leaving the grid.
    wantFocus.current = move !== undefined;
    if (move === 'add-down') {
      if (focus.r === rows.length - 1) {
        if (onAddRow) {
          pendingRow.current = focus.r + 1;
          onAddRow();
        }
      } else {
        moveFocus(1, 0);
      }
    } else if (move) {
      moveFocus(move.dr, move.dc);
    }
  }

  function cancelEdit(): void {
    setEdit(null);
    wantFocus.current = true;
  }

  const activeRow = rows[focus.r];
  const activeCol = columns[focus.c];
  const suggestions =
    edit && !edit.suggestClosed && activeRow && activeCol?.getSuggestions
      ? activeCol.getSuggestions(edit.draft, activeRow).slice(0, 8)
      : [];

  function pickSuggestion(index: number): void {
    const row = rows[focus.r];
    const col = columns[focus.c];
    const suggestion = suggestions[index];
    if (!row || !col || !suggestion) return;
    if (onSuggestionPick) onSuggestionPick(row.id, col.key, suggestion);
    else onCellCommit(row.id, col.key, suggestion.label);
    setEdit(null);
    wantFocus.current = true;
  }

  function handleCellKeyDown(event: KeyboardEvent<HTMLDivElement>, r: number, c: number): void {
    if (edit) return; // input handles its own keys
    const { key } = event;
    const nav: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    if (nav[key]) {
      event.preventDefault();
      moveFocus(nav[key][0], nav[key][1]);
      return;
    }
    if (key === 'Home' || key === 'End') {
      event.preventDefault();
      const meta = event.metaKey || event.ctrlKey;
      setFocus({
        r: meta ? (key === 'Home' ? 0 : rows.length - 1) : r,
        c: key === 'Home' ? 0 : columns.length - 1,
      });
      wantFocus.current = true;
      return;
    }
    if ((event.metaKey && key === 'Backspace') || (event.ctrlKey && key === 'Delete')) {
      event.preventDefault();
      const row = rows[r];
      if (row && onDeleteRow) onDeleteRow(row.id);
      return;
    }
    if (key === 'Enter' || key === 'F2') {
      event.preventDefault();
      const row = rows[r];
      const col = columns[c];
      if (row && col && !colEditable(col, row)) {
        // readonly cell — Enter still advances down the sheet
        if (key === 'Enter') moveFocus(1, 0);
        return;
      }
      beginEdit(null, false);
      return;
    }
    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();
      beginEdit('', true);
      return;
    }
    if (key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      beginEdit(key, true);
    }
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (!edit) return;
    const { key } = event;
    const hasSuggest = suggestions.length > 0;

    if (key === 'Enter') {
      event.preventDefault();
      if (hasSuggest && activeCol?.getSuggestions) {
        pickSuggestion(edit.suggestActive);
        return;
      }
      commitEdit(event.shiftKey ? { dr: -1, dc: 0 } : 'add-down');
      return;
    }
    if (key === 'Tab') {
      event.preventDefault();
      commitEdit({ dr: 0, dc: event.shiftKey ? -1 : 1 });
      return;
    }
    if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (hasSuggest) {
        // First Escape closes the picker; the second cancels the edit.
        setEdit((prev) => (prev ? { ...prev, suggestClosed: true } : prev));
      } else {
        cancelEdit();
      }
      return;
    }
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      if (hasSuggest) {
        event.preventDefault();
        const delta = key === 'ArrowDown' ? 1 : -1;
        setEdit((prev) =>
          prev
            ? {
                ...prev,
                suggestActive:
                  (prev.suggestActive + delta + suggestions.length) % suggestions.length,
              }
            : prev,
        );
        return;
      }
      if (edit.viaTyping) {
        event.preventDefault();
        commitEdit({ dr: key === 'ArrowDown' ? 1 : -1, dc: 0 });
      }
    }
  }

  function restDisplay(col: LineGridColumn<Row>, row: Row): ReactNode {
    if (col.format) return col.format(row);
    const value = col.getValue(row);
    if (value === null || value === '') {
      return col.placeholder ? (
        <span className="bl-lig__placeholder">{col.placeholder}</span>
      ) : null;
    }
    if (col.kind === 'currency') return formatCurrency(typeof value === 'number' ? value : null);
    if (col.kind === 'number') {
      return formatNumber(typeof value === 'number' ? value : null, {
        decimals: col.decimals ?? 2,
        trim: true,
      });
    }
    return String(value);
  }

  const suggestId = `${uid}-suggest`;

  return (
    <div className={cx('bl-lig', className)}>
      <div className="bl-lig__scroll">
        <div role="grid" aria-label={ariaLabel} aria-rowcount={rows.length}>
          <div className="bl-lig__row" role="row" style={{ gridTemplateColumns: template }}>
            <div className="bl-lig__hcell" role="columnheader" aria-label="Line number">
              #
            </div>
            {columns.map((col) => {
              const end = col.align === 'end' || (!col.align && col.kind !== 'text' && col.kind);
              return (
                <div
                  key={col.key}
                  className={cx('bl-lig__hcell', end && 'is-end')}
                  role="columnheader"
                >
                  {col.header}
                </div>
              );
            })}
            {onDeleteRow && <div className="bl-lig__hcell" role="columnheader" />}
          </div>

          {rows.length === 0 && <div className="bl-lig__empty">{emptyHint}</div>}

          {rows.map((row, r) => {
            const tone = rowTone?.(row);
            return (
              <div
                key={row.id}
                className={cx(
                  'bl-lig__row',
                  'bl-lig__row--body',
                  tone && tone !== 'default' && `bl-lig__row--${tone}`,
                )}
                role="row"
                aria-rowindex={r + 1}
                style={{ gridTemplateColumns: template }}
              >
                <div className="bl-lig__gutter bl-num" aria-hidden="true">
                  {r + 1}
                </div>
                {columns.map((col, c) => {
                  const isFocusCell = focus.r === r && focus.c === c;
                  const isEditing = isFocusCell && edit !== null;
                  const numeric = col.kind === 'number' || col.kind === 'currency';
                  const end = col.align === 'end' || (!col.align && numeric);
                  const editable = colEditable(col, row);
                  return (
                    <div
                      key={col.key}
                      ref={(el) => {
                        const mapKey = `${r}:${c}`;
                        if (el) cellRefs.current.set(mapKey, el);
                        else cellRefs.current.delete(mapKey);
                      }}
                      role="gridcell"
                      aria-colindex={c + 1}
                      aria-readonly={!editable || undefined}
                      tabIndex={isFocusCell ? 0 : -1}
                      className={cx(
                        'bl-lig__cell',
                        end && 'is-end',
                        numeric && 'is-num',
                        !editable && 'is-readonly',
                        isEditing && 'is-editing',
                      )}
                      onFocus={() => {
                        if (!isFocusCell) setFocus({ r, c });
                      }}
                      onKeyDown={(event) => handleCellKeyDown(event, r, c)}
                      onDoubleClick={() => {
                        setFocus({ r, c });
                        if (editable) beginEdit(null, false);
                      }}
                    >
                      {isEditing ? (
                        <>
                          <input
                            ref={inputRef}
                            className={cx('bl-lig__input', end && 'is-end')}
                            value={edit.draft}
                            aria-label={`Row ${r + 1} ${typeof col.header === 'string' ? col.header : col.key}`}
                            aria-autocomplete={col.getSuggestions ? 'list' : undefined}
                            aria-expanded={col.getSuggestions ? suggestions.length > 0 : undefined}
                            aria-controls={col.getSuggestions ? suggestId : undefined}
                            onChange={(event) => {
                              const draft = event.currentTarget.value;
                              setEdit((prev) =>
                                prev
                                  ? { ...prev, draft, suggestActive: 0, suggestClosed: false }
                                  : prev,
                              );
                            }}
                            onKeyDown={handleInputKeyDown}
                            onBlur={(event) => {
                              // Ignore blur caused by clicking a suggestion.
                              if (
                                event.relatedTarget &&
                                event.currentTarget.parentElement?.contains(event.relatedTarget)
                              ) {
                                return;
                              }
                              commitEdit();
                            }}
                          />
                          {col.getSuggestions && suggestions.length > 0 && (
                            <div
                              className="bl-lig__suggest"
                              id={suggestId}
                              role="listbox"
                              aria-label="Library matches"
                            >
                              {suggestions.map((s, i) => {
                                const showGroup = i === 0 || suggestions[i - 1]?.group !== s.group;
                                return (
                                  <div key={s.id}>
                                    {showGroup && s.group && (
                                      <div className="bl-lig__suggest-group">{s.group}</div>
                                    )}
                                    <div
                                      role="option"
                                      aria-selected={i === edit.suggestActive}
                                      className={cx(
                                        'bl-lig__suggest-item',
                                        i === edit.suggestActive && 'is-active',
                                      )}
                                      onPointerMove={() =>
                                        setEdit((prev) =>
                                          prev ? { ...prev, suggestActive: i } : prev,
                                        )
                                      }
                                      onPointerDown={(event) => event.preventDefault()}
                                      onClick={() => pickSuggestion(i)}
                                    >
                                      <span className="bl-lig__suggest-label">{s.label}</span>
                                      {s.detail && (
                                        <span className="bl-lig__suggest-detail">{s.detail}</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="bl-lig__display">{restDisplay(col, row)}</span>
                      )}
                    </div>
                  );
                })}
                {onDeleteRow && (
                  <div className="bl-lig__rowaction" role="gridcell">
                    <button
                      type="button"
                      className="bl-lig__delete"
                      tabIndex={-1}
                      aria-label={`Delete row ${r + 1}`}
                      onClick={() => onDeleteRow(row.id)}
                    >
                      <IconClose size={13} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {onAddRow && (
        <button
          type="button"
          className="bl-lig__add"
          onClick={() => {
            pendingRow.current = rows.length;
            onAddRow();
          }}
        >
          <IconPlus size={13} />
          {addLabel}
          <span className="bl-kbd">{isMac() ? '⏎' : 'Enter'}</span>
        </button>
      )}

      {footer != null && <div className="bl-lig__footer">{footer}</div>}
    </div>
  );
}
